import { ConfigProvider, message, Switch } from "antd";
import { MessageInstance } from "antd/es/message/interface";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { MdOutlineLightMode, MdOutlineNightlight } from "react-icons/md";
import DarkTheme from "../themes/dark.json";
import LightTheme from "../themes/light.json";

export type TTheme = "DARK" | "LIGHT";

export const ThemeContext = createContext<{
  theme: TTheme;
  setTheme: (data: TTheme) => void;
  message: MessageInstance;
}>({
  message: {} as MessageInstance,
  theme: "DARK",
  setTheme(data) {},
});

const THEME_STORAGE_KEY = "user-theme-preference";

const getInitialTheme = (): TTheme => {
  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === "DARK" || storedTheme === "LIGHT") {
      return storedTheme;
    }
  } catch (error) {
    console.error("Error reading theme from localStorage during init:", error);
  }
  return "DARK";
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [messageApi, contextHolder] = message.useMessage();

  const [currentTheme, setThemeInternal] = useState<TTheme>(getInitialTheme);

  const setTheme = (newTheme: TTheme) => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    } catch (error) {
      console.error("Error saving theme to localStorage:", error);
    }
    setThemeInternal(newTheme);
  };

  const currentAntdThemeConfig = useMemo(() => {
    return currentTheme === "DARK" ? DarkTheme : LightTheme;
  }, [currentTheme]);

  const contextValue = useMemo(
    () => ({
      message: messageApi,
      theme: currentTheme,
      setTheme: setTheme,
    }),
    [messageApi, currentTheme]
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event: MediaQueryListEvent) => {
      setTheme(event.matches ? "DARK" : "LIGHT");
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return (
    <ThemeContext.Provider value={contextValue}>
      {contextHolder}
      <ConfigProvider theme={currentAntdThemeConfig as any}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();

  return (
    <Switch
      unCheckedChildren={
        <div
          style={{
            height: "23px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "-5px",
          }}
        >
          <MdOutlineNightlight color="#fff" size="18px" />
        </div>
      }
      checkedChildren={
        <div
          style={{
            height: "23px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "-5px",
          }}
        >
          <MdOutlineLightMode color="#000" size="16px" />
        </div>
      }
      checked={theme === "DARK"}
      onChange={(checked) => setTheme(checked ? "DARK" : "LIGHT")}
    />
  );
}
